import React from 'react';
import { cloneDeep, isEmpty, find, get, merge, map } from 'lodash';

// React view component to render
import { wellViewAdministration as WellViewAdministrationView } from 'ui/views/widgets/wellViewAdministration';
import { WellViewAdministrationService } from 'services/wellViewAdministration';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import { getVisibleItems } from 'resolvers/getVisibleEntities';
import * as successOverlay from 'utils/successOverlay';

/**
 * @type { class }
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { WellViewAdministration } from 'ui/containers/WellViewAdministration';
 *
 * // render
 * <WellViewAdministration propName='propValue'/>
 */
export class WellViewAdministration extends React.Component {

	/**
	 * @param { object } props - props
	 */
	constructor( props ) {
		super( props );

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			settingsInfo: undefined,

			// for the save settings
			cagSelected: undefined,
            isFormEdited: false,
        };

		this.handleCagChange = this.handleCagChange.bind( this );
		this.handleActionClick = this.handleActionClick.bind( this );
		this.editActionHandler = this.editActionHandler.bind( this );
		this.handleCancelClick = this.handleCancelClick.bind( this );
		this.handleSaveClick = this.handleSaveClick.bind( this );

		// create service instance
		this.wellViewAdministrationService = new WellViewAdministrationService();

		// start component data fetch
		this.fetchComponentData();
	}

	/*********************************************/

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'WellViewAdministration.fetchComponentData()' );

		// cancel ongoing request
		this.wellViewAdministrationService.cancel();

		// get status and service data
		const {
			status,
			data,
		} = await this.wellViewAdministrationService.fetchComponentData( {
			url: this.props.widget.url,
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
    }
    
    /*------------------------- */

	// handle cag change
	async handleCagChange( { value, url } ) {
		log.debug( 'WellViewAdministration.handleCagChange()' );

		if ( this.state.isFormEdited ) {
			const cancelAction = find( this.state.componentData.actions, {
				name: 'cancel',
			} );
			const shouldProceed = await confirmOverlay.open( {
				message: getLabel( cancelAction.confirmation ),
			} );
			if ( !shouldProceed ) {
				this.fetchSettingsInfo( { value, url } );
			}
		} else {
			this.fetchSettingsInfo( { value, url } );
		}
    }
    
    /*------------------------- */

	// fetch the settings info
	async fetchSettingsInfo( { value, url } ) {
		log.debug( 'WellViewAdministration.fetchSettingsInfo()' );

		// cancel ongoing request
		this.wellViewAdministrationService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const {
			status,
			data,
		} = await this.wellViewAdministrationService.fetchSettingsInfo( {
			url: url,
			data: {
				context: {},
				data: {
					cag: value,
				},
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				settingsInfo: data,
				cagSelected: value,
				isFormEdited: false,
			} );
		}
    }
    
    /*------------------------- */

	// handle cancel click
	async handleCancelClick() {
		log.debug(
			'WellViewAdministration.handleCancelClick()',
			this.state.cagSelected
		);

		// if Form is ever edited then show the confirmation
		if ( this.state.isFormEdited ) {
			const cancelAction = find( this.state.componentData.actions, {
				name: 'cancel',
			} );
			const shouldProceed = await confirmOverlay.open( {
				message: getLabel( cancelAction.confirmation ),
			} );

			if ( !shouldProceed ) {
				this.setState(
					{
						cagSelected: undefined,
						isFetching: true,
						networkStatus: undefined,
						settingsInfo: undefined,
						isFormEdited: false,
					},
					() => this.fetchComponentData()
				);
			}
		} else {

			// if Form is never edited then don't show the confirmation
			this.setState(
				{
					cagSelected: undefined,
					isFetching: true,
					networkStatus: undefined,
					settingsInfo: undefined,
					isFormEdited: false,
				},
				() => this.fetchComponentData()
			);
		}
    }
    
	/*------------------------- */
	
	isObjectEmpty( obj ) {
		let result = true;
		for ( const key in obj ) {
			! isEmpty( obj[ key ] ) ?
				result = false: null;
		}
		return result;
	}

	// Handle any update locally
	handleActionClick( { type, data } ) {
		log.debug( 'WellViewAdministration.handleActionClick()', data );

		const settingsInfo = cloneDeep( this.state.settingsInfo );

		if ( 'contact' === type ) {
			const items = settingsInfo.settingInformation.contactInformation.items;

			// find the item
			const item = find( items, { id: data.id } );

			// update item
			// item.status = isEmpty( data.status ) ? item.status : data.status;
			item.contact = isEmpty( data.contact ) ? item.contact : data.contact;
		}

		if ( 'splashPageContent' === type ) {
			if ( !isEmpty( data.status ) ) {
				if ( this.isObjectEmpty( settingsInfo.settingInformation.splashPageContent.items )  && isEmpty( data.items ) ) {
					this.editActionHandler( { type: 'content', contentStatus: data.status } );
					
				} else {
					settingsInfo.settingInformation.splashPageContent.status = data.status;
				}
			}
			settingsInfo.settingInformation.splashPageContent.items = isEmpty(
				data.items
			)
				? settingsInfo.settingInformation.splashPageContent.items
				: data.items;
		}

		if ( 'whiteLabel' === type ) {
			if( ! isEmpty( data.action ) ) {
				if( 'delete' === data.action ) {
					settingsInfo.settingInformation.whiteLabel.file = undefined;
				}
				if( 'upload' === data.action ) {
					settingsInfo.settingInformation.whiteLabel.file = data.fileName;
				}
			}

			settingsInfo.settingInformation.whiteLabel.status = isEmpty( data.status )
				? settingsInfo.settingInformation.whiteLabel.status
				: data.status;
		}

		this.setState( {
			settingsInfo: settingsInfo,
			isFormEdited: true,
		} );
    }
    
    /*------------------------- */

	// handle when Save got clicked
	async handleSaveClick() {
		log.debug( 'WellViewAdministration.handleSaveClick()' );

		// cancel ongoing request
		this.wellViewAdministrationService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		const saveAction = find( this.state.componentData.actions, {
			name: 'save',
		} );

		const information = cloneDeep(
			get( this.state, 'settingsInfo.settingInformation', {} )
		);

		const InputParam = {
			cag: this.state.cagSelected,
			logo: {
				status: get( information, 'whiteLabel.status' ),
				logoName: get( information, 'whiteLabel.items.logoName' ),
			},
			contact: map( information.contactInformation.items, ( item ) => {
				return {
					id: item.id,
					contact: item.contact,
					status: item.status,
					type: item.type
				};
			} ),
			splashPageContent: merge(
				{ status: get( information, 'splashPageContent.status' ) },
				get( information, 'splashPageContent.items' )
			),
		};

		// get status and service data
		const {
			status,
		} = await this.wellViewAdministrationService.saveSettingsInfo( {
			url: saveAction.url,
			data: {
				context: {},
				data: InputParam,
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );

		} else {
			successOverlay.open( {
				message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
			} );

			this.setState(
				{
					cagSelected: undefined,
					isFetching: true,
					networkStatus: undefined,
					settingsInfo: undefined,
					isFormEdited: false,
				},
				() => this.fetchComponentData()
			);
		}
    }
    
    /*------------------------- */

	// on click the edit of Contact Info or Content Info
	editActionHandler( { id, title, contact, type, contentStatus } = {} ) {
		log.debug( 'WellViewAdministration.editActionHandler()' );

		const updateAction = find(
			get( this.state.settingsInfo.subWidgets, 'actionsConfig', [] ),
			{ name: 'update' }
		);
		const cancelAction = find(
			get( this.state.settingsInfo.subWidgets, 'actionsConfig', [] ),
			{ name: 'cancel' }
		);

		'contact' === type
			? overlay.open( {
					settings: {
						modalType: OVERLAY_VIEWS.EDIT_ADMIN_CONTACTS,
						showCloseButton: false,
						ignoreOutsideClick: false,
					},
					config: {},
					data: {
						itemData: { title, contact },
						actions: [ cancelAction, updateAction ],
						fields: this.state.settingsInfo.subWidgets.contactInformation
							.editContactInformationForm.fields,
						onCancelClick: () => {
							overlay.close();
						},
						onSumbitClick: async ( { contactNo } ) => {
							overlay.close();
							this.handleActionClick( {
								type: 'contact',
								data: {
									id: id,
									contact: contactNo,
								},
							} );
						},
					},
                } )
			: overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.EDIT_SPLASH_PAGE_CONTENT,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    itemData: this.state.settingsInfo.settingInformation
                        .splashPageContent.items,
					actions: [ cancelAction, updateAction ],
					options: this.state.settingsInfo.options,
					title: this.state.settingsInfo.subWidgets.splashPageContent.splashPageContentForm.title,
                    formContent: getVisibleItems( this.state.settingsInfo.subWidgets.splashPageContent.splashPageContentForm.fields, this.state.settingsInfo.visibility ),
                    onCancelClick: () => {
                        overlay.close();
                    },
                    onSumbitClick: async ( values ) => {
                        overlay.close();
                        this.handleActionClick(
                            {
                                type: 'splashPageContent',
                                data: { items: values, status: contentStatus },
                            }
                        );
					},
				},
		} );
	}

	/*********************************************/

	/**
	 * @desc When component unMounts, cleanup resources.
	 */
	componentWillUnmount() {
		this.wellViewAdministrationService.cancel(); // cancel ongoing requests
	}

	/**
	 * @desc Render a React view component
	 */
	render() {
		const showContentDiv = this.isObjectEmpty( get( this.state, 'settingsInfo.settingInformation.splashPageContent.items', {} ) );
		return (
			<WellViewAdministrationView
				isFetching={ this.state.isFetching }
				networkStatus={ this.state.networkStatus }
				onCagSelect={ this.handleCagChange }
				cagSelected={ this.state.cagSelected }
				{ ...this.state.componentData }
				{ ...this.state.settingsInfo }
				onActionClick={ this.handleActionClick }
				onEditClick={ this.editActionHandler }
				onClickCancel={ this.handleCancelClick }
				onSaveClick={ this.handleSaveClick }
				showContentDiv={ ! showContentDiv }
			/>
		);
	}
 }

// set display name
WellViewAdministration.displayName = 'WellViewAdministration';

// set default props
WellViewAdministration.defaultProps = {};

// prop types
WellViewAdministration.propTypes = {};
